import { useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { useParams } from 'next/navigation';

import { callService } from '@api/caller';
import {
  convertDateToMomentObject,
  DateType,
  formatMomentDate,
  HOTEL_SEARCH_DATE_FORMAT,
  URL_DATE_FORMAT,
} from '@utils/dateUtil';
import clean from '@utils/cleanObject';
import STATIC_ROUTES from '@data/staticRoutes.json';
import useSearchBar from '@contexts/searchbar';
import { useAppRouter } from '@contexts/router';
import { useSearchParamsToQuery } from '@hooks/useSearchParamsToQuery';

export const useHotelAvailableDates = ({ roomId }: { roomId: number }) => {
  const query = useSearchParamsToQuery();
  const { push } = useAppRouter();
  const params = useParams();
  const {
    state: { hotel: state },
  } = useSearchBar();

  const [visibleAvailableDates, setVisibleAvailableDates] = useState(false);
  const [startDate, setStartDate] = useState<DateType | null>(
    state.checkDates?.startDate || null
  );
  const [endDate, setEndDate] = useState<DateType | null>(
    state.checkDates?.endDate || null
  );

  //======================================
  // Getting the available dates from the backend
  //======================================
  const checkInDate = moment().format(URL_DATE_FORMAT);
  const checkOutDate = moment().add(6, 'months').format(URL_DATE_FORMAT);

  const { data: availableDates, isFetching } = useQuery(
    ['availableHotelDates', roomId],
    () =>
      callService('HotelsService', 'getAvailability', {
        _in: checkInDate,
        out: checkOutDate,
        roomId,
      }),
    { enabled: Boolean(roomId && visibleAvailableDates) }
  );

  //=================================================
  // Reset the start and end dates when the query changes
  //=================================================
  useEffect(() => {
    setTimeout(() => {
      setStartDate(
        query.in
          ? convertDateToMomentObject(
              query.in as string,
              HOTEL_SEARCH_DATE_FORMAT
            )
          : null
      );
      setEndDate(
        query.out
          ? convertDateToMomentObject(
              query.out as string,
              HOTEL_SEARCH_DATE_FORMAT
            )
          : null
      );
    }, 150); // bypass the ant-design popover time for closing the popover
  }, [query, visibleAvailableDates]);

  //======================================
  // Handle search button
  //======================================
  const handleSearch = () => {
    setVisibleAvailableDates(false);
    push({
      pathname: `${STATIC_ROUTES.hotelPathPrefix}/${params?.slug}`,
      query: clean({
        ...query,
        in: formatMomentDate(startDate, HOTEL_SEARCH_DATE_FORMAT),
        out: formatMomentDate(endDate, HOTEL_SEARCH_DATE_FORMAT),
      }),
    });
  };

  return {
    handleSearch,
    availableDates,
    isFetching,
    setEndDate,
    setStartDate,
    startDate,
    endDate,
    visibleAvailableDates,
    setVisibleAvailableDates,
  };
};
